import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * pointerType is not enough: iOS Safari fakes mouse-flavoured pointer events
 * when a finger lands, so touch passed every hover check.
 */
const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];
  #swatchLockUntil = 0;
  #hoverRestoreMediaId = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.#captureSwatchClick); // ← Move to FIRST
    this.addEventListener('change', this.handleCardSwatchChange, true);
    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('pointerdown', this.#signalSwatchInteraction, true);

    // OAT: Some static product cards mount with NO active slide (all aria-hidden=true).
    // Force an initial selection so the first image is visible without any swatch click.
    setTimeout(() => {
      const ss = this.querySelector('slideshow-component');
      if (!ss || typeof ss.select !== 'function') return;

      // If already active, do nothing
      const active = ss.querySelector('slideshow-slide[aria-hidden="false"]');
      if (active) return;

      // Prefer checked swatch media if present
      const checked = this.querySelector('input[type="radio"][data-option-media-id]:checked');
      const preferredId = checked?.dataset?.optionMediaId;

      // Otherwise fall back to first slide
      const firstSlide = ss.querySelector('slideshow-slide[slide-id]');
      const fallbackId = firstSlide?.getAttribute('slide-id');

      const id = String(preferredId || fallbackId || '');
      if (!id) return;

      try {
        ss.select({ id }, undefined, { animate: false });
      } catch (_) {}

      // Ensure the now-active slide isn't display:none due to any CSS rule
      const nowActive = ss.querySelector(`slideshow-slide[slide-id="${id}"]`);
      if (nowActive) nowActive.setAttribute('aria-hidden', 'false');

      // Kick image load
      const img = nowActive?.querySelector('img');
      if (img) {
        try { img.loading = 'eager'; } catch (_) {}
        try { img.fetchPriority = 'high'; } catch (_) {}
      }
    }, 0);

    requestAnimationFrame(() => {
      const checked = this.querySelector('input[type="radio"][data-option-media-id]:checked');
      const id = checked?.dataset?.optionMediaId;
      if (id) {
        this.#hardShowSlide(String(id));
      }
    });

    requestAnimationFrame(() => {
      const vid = this.#getSelectedVariantIdFromSwatches();
      if (vid) this.#syncAllVariantLinks(vid);
    });

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);

    // --- swatch ownership cleanup ---
    this.removeEventListener('change', this.handleCardSwatchChange, true);
    this.removeEventListener('click', this.#captureSwatchClick, true);
    this.removeEventListener('pointerdown', this.#signalSwatchInteraction, true);

  }

  #normalizeHrefAttr(a) {
    // Some of your anchors have href="\n/products/..." (see productTitleLink in HTML)
    // Always read attribute and trim; fall back to .href if needed.
    const raw = a.getAttribute('href');
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (t) return t;
    }
    // As a last resort, if element has an absolute resolved href, convert to path+query+hash
    try {
      const u = new URL(a.href);
      return u.pathname + u.search + u.hash;
    } catch (_) {}
    return '';
  }

  #isProductNavAnchor(a) {
    if (!(a instanceof HTMLAnchorElement)) return false;

    // Guard: ignore action buttons/overlays that are not PDP nav
    if (
      a.closest('[data-oat-wishlist]') ||
      a.closest('[data-oat-wishlist-root]') ||
      a.closest('[data-oat-quickadd-trigger]') ||
      a.closest('quick-add-component') ||
      a.closest('[data-oat-tryon]') ||
      a.closest('.oat-tile-overlay__icon') ||
      a.closest('.oat-tryon')
    ) return false;

    const href = this.#normalizeHrefAttr(a);
    if (!href) return false;

    // Only internal product links (relative)
    if (href[0] !== '/') return false;

    // Deterministic allowlist: your three refs + overlay class + /products/ path
    const ref = a.getAttribute('ref') || '';
    if (ref === 'productCardLink') return true;
    if (ref === 'cardGalleryLink') return true;
    if (ref === 'productTitleLink') return true;
    if (a.classList.contains('product-card__link')) return true;
    if (href.includes('/products/')) return true;

    return false;
  }

  #syncAllVariantLinks(variantId) {
    const id = Number(variantId);
    if (!id) return;

    const anchors = Array.from(this.querySelectorAll('a[href]')).filter(a => this.#isProductNavAnchor(a));

    for (const a of anchors) {
      const href = this.#normalizeHrefAttr(a);
      if (!href) continue;

      const u = new URL(href, window.location.origin);
      u.searchParams.set('variant', String(id));

      const next = u.pathname + u.search + u.hash;

      // Preserve attribute style (relative). Also removes accidental leading whitespace.
      a.setAttribute('href', next);
    }

    // Optional: keep a card-level memory for debugging
    this.dataset.oatSelectedVariantId = String(id);
  }

  #getSelectedVariantIdFromSwatches() {
    // Your swatch inputs include data-variant-id (this is what we need)
    const checked = this.querySelector('input[type="radio"][data-variant-id]:checked');
    const id = Number(checked?.dataset?.variantId || 0);
    return id || 0;
  }

  #getSelectedMediaIdFromSwatches() {
    const checked = this.querySelector(
      'swatches-variant-picker-component input[type="radio"][data-option-media-id]:checked'
    );
    return checked?.dataset?.optionMediaId || null;
  }

  #getVisibleMediaId() {
    const slide = Array.from(this.querySelectorAll('slideshow-slide[slide-id]')).find(
      item => !item.hidden && item.getAttribute('aria-hidden') !== 'true' && getComputedStyle(item).display !== 'none'
    );
    return slide?.getAttribute('slide-id') || null;
  }

  #hardShowSlide(id) {
    const slides = Array.from(this.querySelectorAll('slideshow-slide[slide-id]'));
    if (!slides.length) return;

    const target = slides.find(s => s.getAttribute('slide-id') === String(id));
    if (!target) return;

    for (const s of slides) {
      const on = s === target;

      s.hidden = !on;
      s.toggleAttribute('hidden', !on);
      s.setAttribute('aria-hidden', on ? 'false' : 'true');
      s.style.display = on ? '' : 'none';
    }
  }

  #signalSwatchInteraction = (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;

    const inPicker = el.closest('swatches-variant-picker-component');
    if (!inPicker) return;

    const isSwatchThing =
      el.matches('input[type="radio"][data-option-media-id], label[data-media-id]') ||
      !!el.closest('input[type="radio"][data-option-media-id], label[data-media-id]');

    if (!isSwatchThing) return;

    this.dispatchEvent(new CustomEvent('oat:swatch-click', {
      bubbles: true,
      composed: true,
      detail: { ts: performance.now() }
    }));
  };

  #captureSwatchClick = (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;

    const stopNav = el.closest('[data-oat-stop-card-nav]');
    if (!stopNav) return;
    
    // Dispatch custom event that bubbles up to product-card-link
    this.dispatchEvent(new CustomEvent('oat:swatch-click', {
      bubbles: true,
      composed: true,
      detail: { ts: performance.now() }
    }));
    
    // Don't interfere with the click at all
  };

  #swatchOverlayForwarder = (event) => {
    if (!(event.target instanceof Element)) return;

    // Common overlay targets
    const overlay =
      event.target.closest('a.product-card__link') ||
      event.target.closest('product-card-link') ||
      event.target.closest('a[href]');

    if (!overlay) return;

    const x = event.clientX;
    const y = event.clientY;
    if (typeof x !== 'number' || typeof y !== 'number') return;

    // See what is actually under pointer
    const prev = overlay.style.pointerEvents;
    overlay.style.pointerEvents = 'none';
    const under = document.elementFromPoint(x, y);
    overlay.style.pointerEvents = prev;

    const swatchInput =
      under?.matches?.('input[type="radio"][data-option-media-id]') ? under :
      under?.closest?.('label')?.querySelector?.('input[type="radio"][data-option-media-id]');

    if (!swatchInput) return;
    if (!swatchInput.closest('swatches-variant-picker-component')) return;

    // Kill nav + forward click to real radio (so change triggers)
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    swatchInput.click();
  };

  handleCardSwatchChange = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.type !== 'radio') return;
    if (!t.dataset.optionMediaId) return;

    // OAT: prevent previewVariant from overriding a deliberate swatch change
    this.#swatchLockUntil = performance.now() + 800;

    this.selectVariant(String(t.dataset.optionMediaId));

    const vid = this.#getSelectedVariantIdFromSwatches();
    if (vid) this.#syncAllVariantLinks(vid);
  };

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  };

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    // --- OAT: After morph, re-select the swatch media on the new slideshow DOM ---
    const selectedMediaId = this.variantPicker?.selectedOption?.dataset?.optionMediaId;
    if (selectedMediaId && this.refs?.slideshow) {
      const id = String(selectedMediaId);
      const slideshow = this.refs.slideshow;

      // Let morph + custom elements settle
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          try { slideshow.removeAttribute('disabled'); } catch (_) {}
          try { slideshow.disabled = false; } catch (_) {}

          try {
            const selectedMediaId = this.variantPicker?.selectedOption?.dataset?.optionMediaId;
            if (selectedMediaId) {
              queueMicrotask(() =>
                requestAnimationFrame(() =>
                  this.#hardShowSlide(String(selectedMediaId))
                )
              );
            }
          } catch (_) {}
        });
      });
    }

    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const selectedImageId = this.#getSelectedMediaIdFromSwatches();
    if (!selectedImageId) return;

    const id = String(selectedImageId);

    // Let Horizon slideshow own visibility. Never set hidden/display on slides.
    try {
      slideshow.select({ id }, undefined, { animate: false });
    } catch (_) {}
  }


  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    // Hover only: a finger never fires the pointerleave that undoes this, so
    // a swatch brushed while scrolling left the card wearing the wrong image.
    if (!canHover.matches) return;

    this.resetVariant.cancel();
    if (!this.#hoverRestoreMediaId) this.#hoverRestoreMediaId = this.#getVisibleMediaId();
    this.#hardShowSlide(id);
  }

  selectVariant(id) {
    this.resetVariant.cancel();
    this.#hoverRestoreMediaId = null;
    this.#hardShowSlide(id);

    // IMPORTANT: `id` here is media-id in your current usage (previewVariant/3759...)
    // So DO NOT treat it as variantId.
    // We sync links using the currently checked swatch’s data-variant-id.
    const vid = this.#getSelectedVariantIdFromSwatches();
    if (vid) this.#syncAllVariantLinks(vid);
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse' || !canHover.matches) return;

    const { slideshow, cardGallery } = this.refs;
    if (!slideshow || !cardGallery) return;

    this.resetVariant.cancel();

    const group = (cardGallery.getAttribute('data-oat-selected-group') || '').trim().toLowerCase();

    // No group -> default behavior
    if (!group) {
      slideshow.next(undefined, { animate: false });
      return;
    }

    const slides = slideshow.slides || [];

    for (const slide of slides) {
      const img = slide.querySelector('img');
      if (!img) continue;

      const alt = (img.getAttribute('alt') || '').trim().toLowerCase();

      // EXACT match
      if (alt === group) {
        const slideId = slide.getAttribute('slide-id');
        if (slideId) {
          slideshow.select({ id: slideId }, undefined, { animate: false });
          return;
        }
      }
    }

    // fallback
    slideshow.next(undefined, { animate: false });
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse' || !canHover.matches) return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const selectedId = this.#getSelectedMediaIdFromSwatches();
    const selectedSlide = Array.from(this.querySelectorAll('slideshow-slide[slide-id]')).find(
      slide => slide.getAttribute('slide-id') === String(selectedId)
    );
    const id = selectedSlide ? selectedId : this.#hoverRestoreMediaId;
    this.#hoverRestoreMediaId = null;
    if (id) {
      this.#hardShowSlide(id);
    }
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
     if (event.__swatchHandled) {
      return;
    }
    
    if (event.target.closest('[data-oat-stop-card-nav]')) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, textarea, a, swatches-variant-picker-component, .variant-option--swatches, .variant-option__swatch'
    );

    if (interactiveElement) return;


    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
